import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Eye, ClipboardList, DollarSign, Clock,
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import SourcingRfpService from '../../services/sourcingRFPService';

const statusBadge = (status) => {
  const map = {
    DRAFT:     { bg: '#f3f4f6', color: '#6b7280', label: 'Draft' },
    SUBMITTED: { bg: '#dbeafe', color: '#1d4ed8', label: 'Submitted' },
    UNDER_REVIEW: { bg: '#fef3c7', color: '#b45309', label: 'Under Review' },
    AWARDED:   { bg: '#d1fae5', color: '#059669', label: 'Awarded' },
    REJECTED:  { bg: '#fee2e2', color: '#dc2626', label: 'Not Selected' },
  };
  const s = map[status] || map.DRAFT;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontSize: 12,
      fontWeight: 600, background: s.bg, color: s.color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
      {s.label}
    </span>
  );
};

const fmtDate = (val) => {
  if (!val) return '\u2014';
  try { return new Date(val).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '\u2014'; }
};

const extractResponses = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const VendorMyBids = () => {
  const navigate = useNavigate();
  const { error: showError, toasts, removeToast } = useToast();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadResponses = async () => {
    setLoading(true);
    try {
      const res = await SourcingRfpService.getRfps();
      const rfps = extractResponses(res);
      const allResponses = [];
      for (const rfp of rfps) {
        try {
          const respRes = await SourcingRfpService.getResponses(rfp._id);
          const r = extractResponses(respRes);
          r.forEach(item => allResponses.push({ ...item, rfpTitle: rfp.title, rfpNumber: rfp.rfpNumber }));
        } catch { }
      }
      setResponses(allResponses);
    } catch (err) {
      console.error('Failed to load bids:', err);
      showError('Could not load your bids.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadResponses(); }, []);

  const columns = [
    { key: '__spacer__', label: '', width: '12px', sortable: false, render: () => <div style={{ width: 12 }} /> },
    {
      key: 'rfpNumber', label: 'RFP',
      render: (_, row) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--gold-primary, #b78a00)', fontSize: 13 }}>
          {row.rfpNumber || row.rfpId?.slice(-8).toUpperCase() || 'N/A'}
        </span>
      ),
    },
    {
      key: 'rfpTitle', label: 'Project',
      render: (_, row) => (
        <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>{row.rfpTitle || 'Untitled'}</span>
      ),
    },
    {
      key: 'totalAmount', label: 'Bid Amount',
      render: (_, row) => {
        const total = row.lineItems?.reduce((s, i) => s + (i.price || 0), 0) || row.totalAmount;
        return (
          <div className="flex items-center gap-1" style={{ color: '#059669', fontWeight: 700 }}>
            <DollarSign size={14} /> {total ? Number(total).toLocaleString() : '\u2014'}
          </div>
        );
      },
    },
    {
      key: 'createdAt', label: 'Submitted',
      render: (_, row) => (
        <div className="flex items-center gap-1.5" style={{ color: '#6b7280', fontSize: 13 }}>
          <Clock size={13} /> {fmtDate(row.createdAt || row.submittedAt)}
        </div>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: (_, row) => statusBadge(row.status),
    },
    {
      key: 'action', label: '', width: '60px',
      render: (_, row) => (
        <button onClick={() => {
          const rfpId = row.rfpId || row.rfp?._id;
          if (rfpId) navigate(`/subcontracting/rfps/bid/${rfpId}`);
        }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 500, fontSize: 12, cursor: 'pointer' }}>
          <Eye size={14} /> View
        </button>
      ),
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="My Bids"
        description="View and manage your submitted proposals"
        actions={[{ icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />, variant: 'outline', onClick: loadResponses }]}
      />
      <div className="page-container">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <DataTable data={responses} columns={columns} loading={loading} showActions={false}
            emptyMessage="You have not submitted any bids yet."
          />
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default VendorMyBids;
